import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaStar, FaCalendarAlt, FaPhone, FaEnvelope, FaLanguage, FaClock, FaDollarSign, FaCheck, FaGraduationCap, FaAward } from 'react-icons/fa';
import { User } from 'lucide-react';
import { FiArrowLeft } from 'react-icons/fi';
import { api } from '../../services/api';

export default function ExpertDetailsPage() {
  const { id } = useParams();
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState('video');
  const [consultationTopic, setConsultationTopic] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [paymentReference, setPaymentReference] = useState(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);



  useEffect(() => {
    const fetchExpertDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/experts/${id}`);
        if (response.data.success) {
          setExpert(response.data.data);
        } else {
          setError('Failed to fetch expert details');
        }
      } catch (err) {
        console.error('Error fetching expert details:', err);
        setError('Failed to load expert details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchExpertDetails();
  }, [id]);

  const handleBooking = async (e) => {
    e.preventDefault();
    
    try {
      setIsBooking(true);
      setError(null);
      
      // Initialize appointment and get payment URL
      const appointmentData = {
        expert_id: expert.expert_id,
        date: selectedDate,
        time: selectedTime,
        description: consultationTopic || "Consultation requested",
        consultation_type: consultationType
      };

      const response = await api.post('/appointments/initialize', appointmentData);
      
      if (response.data.success) {
        const { authorization_url, reference } = response.data.data;
        
        // Store reference for payment verification
        setPaymentReference(reference);
        localStorage.setItem('appointment_reference', reference);
        
        // Open Paystack payment modal
        const paymentWindow = window.open(authorization_url, '_blank', 'width=500,height=600');
        
        // Monitor if payment window is closed without completing payment
        const checkClosed = setInterval(() => {
          if (paymentWindow.closed) {
            clearInterval(checkClosed);
            // If window is closed and we're still verifying, payment was cancelled
            if (isVerifyingPayment) {
              setIsVerifyingPayment(false);
              setPaymentReference(null);
              localStorage.removeItem('appointment_reference');
              setError('Payment could not be completed. Please try again.');
            }
          }
        }, 1000);
        
        // Start polling for payment verification
        pollPaymentVerification(reference);
      } else {
        setError(response.data.message || 'Failed to initialize appointment');
      }
    } catch (err) {
      console.error('Error initializing appointment:', err);
      setError('Failed to initialize appointment. Please try again.');
    } finally {
      setIsBooking(false);
    }
  };

  // Poll for payment verification
  const pollPaymentVerification = useCallback(async (reference) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;
    
    // Show loading overlay when verification starts
    setIsVerifyingPayment(true);
    
    const pollInterval = setInterval(async () => {
      attempts++;
      
      try {
        const verifyResponse = await api.post('/appointments/verify-payment', { reference });
        
        if (verifyResponse.data.success) {
          clearInterval(pollInterval);
          localStorage.removeItem('appointment_reference');
          setPaymentReference(null);
          setIsVerifyingPayment(false);
          
          // Show success message
          setBookingSuccess(true);
          setError(null);
          
          // Reset form after 5 seconds
          setTimeout(() => {
            setBookingSuccess(false);
            setSelectedDate('');
            setSelectedTime('');
            setConsultationType('video');
            setConsultationTopic('');
          }, 10000);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        // If verification fails, stop polling and show error
        if (err.response?.status === 400 || err.response?.status === 422) {
          clearInterval(pollInterval);
          localStorage.removeItem('appointment_reference');
          setPaymentReference(null);
          setIsVerifyingPayment(false);
          setError('Payment could not be completed. Please try again.');
        }
      }
      
      // Stop polling after max attempts
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        localStorage.removeItem('appointment_reference');
        setPaymentReference(null);
        setIsVerifyingPayment(false);
        setError('Payment verification timeout. Please check your appointment status.');
      }
    }, 10000); // Check every 10 seconds
  }, []);

  // Check for pending payment verification on component mount
  useEffect(() => {
    const pendingReference = localStorage.getItem('appointment_reference');
    if (pendingReference) {
      pollPaymentVerification(pendingReference);
    }
  }, [pollPaymentVerification]);

  // Generate available dates based on expert's available days
  const getAvailableDates = () => {
    if (!expert?.available_days) return [];
    
    try {
      const availableDays = JSON.parse(expert.available_days);
      const dates = [];
      const today = new Date();
      
      // Generate dates for the next 30 days
      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(today.getDate() + i);
        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        
        // Only include dates that match the expert's available days
        if (availableDays.includes(dayName)) {
          const formattedDate = date.toISOString().split('T')[0];
          dates.push({
            date: formattedDate,
            display: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          });
        }
      }
      
      return dates;
    } catch {
      return [];
    }
  };

  // Generate time slots based on expert's available time
  const getAvailableTimeSlots = () => {
    if (!expert?.available_time) return [];
    
    try {
      const [startTime, endTime] = expert.available_time.split('-');
      const startHour = parseInt(startTime.split(':')[0]);
      const endHour = parseInt(endTime.split(':')[0]);
      
      const timeSlots = [];
      
      // Generate time slots in 1-hour intervals
      for (let hour = startHour; hour < endHour; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:00`;
        timeSlots.push(time);
      }
      
      return timeSlots;
    } catch {
      return [];
    }
  };

  // Parse available days from JSON string
  const getAvailableDays = () => {
    if (!expert?.available_days) return [];
    try {
      return JSON.parse(expert.available_days);
    } catch {
      return [];
    }
  };



  if (loading) {
    return <div className="loading_apw">Loading expert details...</div>;
  }

  if (error) {
    return (
      <div className="expert-details-container_apw">
        <div className="back-button-container_apw">
          <Link to="/farmer-dashboard/experts" className="back-button_apw">
            <FiArrowLeft /> Back to Experts
          </Link>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  if (!expert) {
    return <div className="loading_apw">Expert not found</div>;
  }

  return (
    <>
      <div className="expert-details-container_apw">
      <div className="back-button-container_apw">
        <Link to="/farmer-dashboard/experts" className="back-button_apw">
          <FiArrowLeft /> Back to Experts
        </Link>
      </div>

      <div className="expert-profile-container_apw">
        <div className="expert-profile-header_apw">
          <div className="expert-image-container_apw">
            {expert.image ? (
              <img src={expert.image} alt={expert.name} className="expert-image_apw" />
            ) : (
              <div className="expert-image-placeholder_apw">
                <User size={80} color="#999" />
              </div>
            )}
          </div>
          
          <div className="expert-header-info_apw">
            <h1 className="expert-name_apw">{expert.name}</h1>
            <h2 className="expert-specialization_apw">{expert.specialization}</h2>
            
            <div className="expert-stats_apw">
              <div className="stat_apw">
                <FaClock className="stat-icon_apw" />
                <span>{expert.years_of_experience} Years Experience</span>
              </div>
              <div className="stat_apw">
                <FaDollarSign className="stat-icon_apw" />
                <span>₦{expert.booking_fee}/hour</span>
              </div>
            </div>

            <div className="expert-availability_apw">
              <div className="schedule-badge_apw">
                Available: {getAvailableDays().join(', ')} {expert.available_time}
              </div>
            </div>
          </div>
        </div>

        <div className="expert-content-container_apw">
          <div className="expert-details-panel_apw">
            {expert.biography && (
              <section className="expert-bio-section_apw">
                <h3 className="section-title_apw">About</h3>
                <p className="expert-bio_apw">{expert.biography}</p>
              </section>
            )}

            {expert.education && expert.education.length > 0 && (
              <section className="expert-details-section_apw">
                <h3 className="section-title_apw">Education</h3>
                <div className="expert-education_apw">
                  {expert.education.map((edu, index) => (
                    <div key={index} className="education-item_apw">
                      <h4>{edu.degree}</h4>
                      <p>{edu.institution}, {edu.year}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {expert.credentials && (
              <section className="expert-details-section_apw">
                <h3 className="section-title_apw">Credentials</h3>
                <div className="expert-credentials_apw">
                  <p><FaAward className="credential-icon_apw" /> {expert.credentials}</p>
                </div>
              </section>
            )}

            {expert.areas_of_expertise && expert.areas_of_expertise.length > 0 && (
              <section className="expert-expertise-section_apw">
                <h3 className="section-title_apw">Areas of Expertise</h3>
                <div className="expertise-tags_apw">
                  {expert.areas_of_expertise.map((item, index) => (
                    <span key={index} className="expertise-tag_apw">{item}</span>
                  ))}
                </div>
              </section>
            )}

            <section className="expert-info-section_apw">
              <h3 className="section-title_apw">Additional Information</h3>
              <div className="info-grid_apw">
                {expert.languages && expert.languages.length > 0 && (
                  <div className="info-item_apw">
                    <FaLanguage className="info-icon_apw" />
                    <div>
                      <h4>Languages</h4>
                      <p>{expert.languages.join(', ')}</p>
                    </div>
                  </div>
                )}
                {expert.email && (
                  <div className="info-item_apw">
                    <FaEnvelope className="info-icon_apw" />
                    <div>
                      <h4>Email</h4>
                      <p>{expert.email}</p>
                    </div>
                  </div>
                )}
                {expert.phone && (
                  <div className="info-item_apw">
                    <FaPhone className="info-icon_apw" />
                    <div>
                      <h4>Phone</h4>
                      <p>{expert.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="booking-panel_apw" style={{ position: 'relative' }}>
            {isVerifyingPayment && (
              <div className="payment-verification-overlay_apw">
                <div className="verification-loader_apw">
                  <div className="spinner_apw"></div>
                  <h4>Payment processing...</h4>
                  <p>Please wait while we confirm your payment</p>
                </div>
              </div>
            )}
            <h3 className="booking-title_apw">Book a Consultation</h3>
            
            {bookingSuccess ? (
              <div className="booking-success_apw">
                <FaCheck className="success-icon_apw" />
                <h4>Booking Successful!</h4>
                <p>Your consultation with {expert.name} has been scheduled for {selectedDate} at {selectedTime}.</p>
                <p className="success-note_apw">You will receive a confirmation email shortly.</p>
              </div>
            ) : (
              <div>
                {error && (
                  <div className="booking-error_apw">
                    <p>{error}</p>
                  </div>
                )}
                {paymentReference && (
                  <div className="payment-pending_apw">
                    <p>Payment window opened. Please complete your payment...</p>
                  </div>
                )}
                <form className="booking-form_apw" onSubmit={handleBooking}>
                <div className="form-group_apw">
                  <label>Select Date</label>
                  <select 
                    value={selectedDate} 
                    onChange={(e) => setSelectedDate(e.target.value)}
                    required
                    className="form-select_apw"
                  >
                    <option value="">Choose a date</option>
                    {getAvailableDates().length > 0 ? (
                      getAvailableDates().map(date => (
                        <option key={date.date} value={date.date}>
                          {date.display}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No available dates in next 30 days</option>
                    )}
                  </select>
                </div>

                <div className="form-group_apw">
                  <label>Select Time</label>
                  <select 
                    value={selectedTime} 
                    onChange={(e) => setSelectedTime(e.target.value)}
                    required
                    className="form-select_apw"
                  >
                    <option value="">Choose a time slot</option>
                    {getAvailableTimeSlots().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group_apw">
                  <label>Consultation Type</label>
                  <div className="consultation-types_apw">
                    <label className={`consultation-type_apw ${consultationType === 'video' ? 'selected_apw' : ''}`}>
                      <input
                        type="radio"
                        name="consultationType"
                        value="video"
                        checked={consultationType === 'video'}
                        onChange={() => setConsultationType('video')}
                      />
                      <span>Video Call</span>
                    </label>
                    <label className={`consultation-type_apw ${consultationType === 'voice' ? 'selected_apw' : ''}`}>
                      <input
                        type="radio"
                        name="consultationType"
                        value="voice"
                        checked={consultationType === 'voice'}
                        onChange={() => setConsultationType('voice')}
                      />
                      <span>Voice Call</span>
                    </label>
                    <label className={`consultation-type_apw ${consultationType === 'chat' ? 'selected_apw' : ''}`}>
                      <input
                        type="radio"
                        name="consultationType"
                        value="chat"
                        checked={consultationType === 'chat'}
                        onChange={() => setConsultationType('chat')}
                      />
                      <span>Chat</span>
                    </label>
                  </div>
                </div>

                <div className="form-group_apw consultation-topic_apw">
                  <label>Topic/Issue</label>
                  <textarea required
                    placeholder="Briefly describe what you'd like to discuss..."
                    className="form-textarea_apw"
                    value={consultationTopic}
                    onChange={(e) => setConsultationTopic(e.target.value)}
                  ></textarea>
                </div>

                <div className="booking-fee_apw">
                  <p>Consultation Fee: <strong>₦{expert.booking_fee}/hour</strong></p>
                </div>

                {!expert.booking_fee || expert.booking_fee == 0 ? (
                  <div className="no-fee-disclaimer_apw">
                    <p>This expert has not set a consultation fee yet. Booking is unavailable until the expert updates their profile.</p>
                  </div>
                ) : null}

                <button type="submit" className="book-button_apw" disabled={isBooking || loading || !expert.booking_fee || expert.booking_fee == 0}>
                  {isBooking ? 'Initializing Payment...' : 'Book Consultation'}
                </button>
                </form>
              </div>
            )}

            <div className="contact-options_apw">
              <h4>Other Contact Options</h4>
              <div className="contact-buttons_apw">
                {expert.email && (
                  <Link 
                    to={`/farmer-dashboard/messages?expert_id=${id}`}
                    className="contact-button_apw"
                    style={{ textDecoration: 'none', display: 'inline-block' }}
                  >
                    <FaEnvelope /> Message
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style jsx>{`
      .booking-error_apw {
        background: #fee2e2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 16px;
        text-align: center;
      }

      .payment-pending_apw {
        background: #fef3c7;
        border: 1px solid #fde68a;
        color: #d97706;
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 16px;
        text-align: center;
      }

      .success-note_apw {
        color: #6b7280;
        font-size: 14px;
        margin-top: 8px;
        font-style: italic;
      }

      .book-button_apw:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .no-fee-disclaimer_apw {
        background: #fff7ed;
        border: 1px solid #fed7aa;
        color: #c2410c;
        padding: 10px 14px;
        border-radius: 8px;
        margin-bottom: 12px;
        font-size: 13px;
        text-align: center;
      }

      /* Payment Verification Overlay */
      .payment-verification-overlay_apw {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        border-radius: 12px;
        backdrop-filter: blur(2px);
      }

      .verification-loader_apw {
        text-align: center;
        padding: 30px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        max-width: 300px;
        width: 90%;
      }

      .spinner_apw {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f4f6;
        border-top: 4px solid #0e4410;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px auto;
      }

      .verification-loader_apw h4 {
        color: #0e4410;
        margin: 0 0 10px 0;
        font-size: 18px;
        font-weight: 600;
      }

      .verification-loader_apw p {
        color: #6b7280;
        margin: 0;
        font-size: 14px;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </>
  );
}